/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';

const TradeContext = createContext();

// Keys used to persist trade context across page loads
const LS_ACCOUNT_KEY = 'tradeContext:accountId';

export const useTrade = () => {
    const context = useContext(TradeContext);
    if (!context) {
        throw new Error('useTrade must be used within a TradeProvider');
    }
    return context;
};

export const TradeProvider = ({ children }) => {
    const [tradeContext, setTradeContext] = useState(() => {
        let savedAccountId = '';
        try { savedAccountId = localStorage.getItem(LS_ACCOUNT_KEY) || ''; } catch (_) {}
        return {
            accountId: savedAccountId,
            symbol: 'EURUSD', // Default symbol
            timeframe: 'M15', // Default timeframe
        };
    });

    // Persist accountId to localStorage so it's instantly available on next load
    useEffect(() => {
        try {
            if (tradeContext.accountId) {
                localStorage.setItem(LS_ACCOUNT_KEY, tradeContext.accountId);
            }
        } catch (_) {}
    }, [tradeContext.accountId]);

    // Reset trade context on logout so the next user doesn't inherit stale state
    useEffect(() => {
        const handleLogout = () => {
            setTradeContext({ accountId: '', symbol: 'EURUSD', timeframe: 'M15' });
        };
        window.addEventListener('paxia:logout', handleLogout);
        return () => window.removeEventListener('paxia:logout', handleLogout);
    }, []);

    const setContext = (newContext) => {
        setTradeContext(prevContext => ({ ...prevContext, ...newContext }));
    };

    const value = useMemo(() => ({
        ...tradeContext,
        setTradeContext: setContext,
    }), [tradeContext]);

    return (
        <TradeContext.Provider value={value}>
            {children}
        </TradeContext.Provider>
    );
};
